import React from 'react';

/**
 * 默认错误组件
 * 高度与 SkeletonLoading 严格一致，避免状态切换时页面闪动
 * 自动向 Sentry 上报详细错误信息
 *
 * @param {Object} props
 * @param {string|Error} props.error - 错误信息或 Error 对象
 * @param {Function} props.onRetry - 重试回调
 * @param {string} props.height - 容器高度，默认取 SkeletonLoading 的 SSR_CONTENT_HEIGHT
 */

var _skeleton = require('./SkeletonLoading');
var SSR_CONTENT_HEIGHT = _skeleton.SSR_CONTENT_HEIGHT;

function DefaultErrorComponent({ error, onRetry }) {
  const [sentryEventId, setSentryEventId] = React.useState(null);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && error) {
      try {
        var Sentry = window.Sentry || (window.__SENTRY__ && window.__SENTRY__.hub);
        if (!Sentry && typeof require !== 'undefined') {
          try { Sentry = require('@sentry/browser'); } catch (e) { /* not available */ }
        }
        if (Sentry && Sentry.captureException) {
          var errorObj = error instanceof Error ? error : new Error(String(error));
          Sentry.withScope(function(scope) {
            scope.setTag('component', 'DefaultErrorComponent');
            scope.setTag('render_mode', window.__RENDER_MODE__ || 'unknown');
            scope.setTag('data_source', window.__DATA_SOURCE__ || 'unknown');
            scope.setLevel('error');
            scope.setContext('page_info', {
              url: window.location.href,
              pathname: window.location.pathname,
              referrer: document.referrer || 'none',
              renderMode: window.__RENDER_MODE__ || 'unknown',
              dataSource: window.__DATA_SOURCE__ || 'unknown',
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            });
            scope.setContext('load_decision', window.__LOAD_DECISION__ || {});
            scope.setContext('error_detail', {
              message: String(error),
              stack: errorObj.stack || 'no stack',
              name: errorObj.name || 'Error'
            });
            var eventId = Sentry.captureException(errorObj);
            setSentryEventId(eventId);
          });
        }
      } catch (e) {
        // Sentry 不可用时静默忽略
      }
    }
  }, [error]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: SSR_CONTENT_HEIGHT,
      boxSizing: 'border-box',
      padding: '40px 24px',
    }}>
      <div style={{
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 24px',
          borderRadius: '50%',
          background: '#fff1f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
          lineHeight: 1,
        }}>
          <span style={{ color: '#ff4d4f' }}>!</span>
        </div>

        <h3 style={{
          margin: '0 0 8px',
          fontSize: '20px',
          fontWeight: 600,
          color: '#1a1a1a',
        }}>Something went wrong</h3>

        <p style={{
          margin: '0 0 16px',
          fontSize: '14px',
          color: '#8c8c8c',
          lineHeight: 1.6,
        }}>
          {error || 'An unexpected error occurred. Please try again later.'}
        </p>

        {sentryEventId && (
          <p style={{
            margin: '0 0 20px',
            fontSize: '12px',
            color: '#bfbfbf',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}>
            Error ID: {sentryEventId}
          </p>
        )}

        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'var(--color-primary, #1a1a1a)',
              color: '#fff',
              border: 'none',
              padding: '10px 28px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export default DefaultErrorComponent;
